import React, { useEffect, useMemo, useState } from "react";
import CreateUi from "./CreateUi";
import { WAY_OF_CONFIRMATION } from "../../utils/beneficiary.constant";
import { beneficiaryFormData, validatePhone } from "../../utils/helper";
import moment from "moment";
import { SNACK_BAR_VARIETNS } from "../../utils/constants";
import { useDispatch } from "react-redux";
import { callApiAction } from "../../store/actions/commonAction";
import { addBeneficiaryApi } from "../../apis/beneficiary.api";
import { useNavigate } from "react-router-dom";
import useValidate from "../../store/hooks/useValidator";
import { callSnackBar } from "../../store/actions/snackbarAction";

const CreateController = ({ id }) => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [fetchLoading, setFetchLoading] = useState(false);

  const defaultFields = {
    err: "",
    id,
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    organization: "",
    department: "",
    designation: "",
    gender: "",
    dob: moment().toISOString(),
    country: "India",
    state: beneficiaryFormData.get("state"),
    district: beneficiaryFormData.get("district"),
    taluka: "",
    has_hearing_loss: false,
    address_line: "",
    photo: "",
    PIN: "",
    type_of_hearing_loss: null,
    severity_of_hearing_loss: null,
    left_severity_of_hearing_loss: null,
    right_severity_of_hearing_loss: null,
    audio_gram_file: null,
    nu_device_responce: true,
    music_responce: true,
    voice_responce: true,
    has_smart_phone: true,
    left_response_in_db: 0,
    right_response_in_db: 0,
    way_of_confirmation: WAY_OF_CONFIRMATION.VERBAL_CONFIRMATION,
    product: beneficiaryFormData.get("product"),
    camp: beneficiaryFormData.get("camp"),
    suggested_products: beneficiaryFormData.get("suggested_product"),
    dignosis: "",
    right_ear_wax: null,
    right_eardrum: null,
    is_discharge_left: null,
    is_discharge_right: null,
    left_ear_wax: null,
    left_eardrum: null,
  };

  const [fields, setFields] = useState(defaultFields);
  const [originalDocument, setOriginalDocument] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const validate = useValidate();

  const getValidationErrors = (schema) => {
    const errors = {};

    for (const field of schema) {
      if (
        field.required &&
        (field.value === undefined || field.value === null || field.value === "")
      ) {
        errors[field.key || field.field] = `${field.field} is required.`;
        continue;
      }

      if (field.custom && typeof field.custom === "function") {
        const customResponse = field.custom();

        if (customResponse !== true) {
          errors[field.key || field.field] = customResponse;
        }
      }
    }

    return errors;
  };

  const validationSchemaForCreate = useMemo(
    () => [
      {
        required: true,
        value: fields.camp,
        field: "Camp ",
        key: "camp",
      },
      {
        required: true,
        value: fields.country,
        field: "Country",
        key: "country",
      },
      {
        required: true,
        value: fields.state,
        field: "State",
        key: "state",
      },
      {
        required: true,
        value: fields.district,
        field: "District",
        key: "district",
      },
      {
        value: fields.taluka,
        field: "Taluka",
        key: "taluka",
      },
      {
        required: true,
        value: fields.first_name,
        field: "First Name",
        key: "first_name",
      },
      {
        required: true,
        value: fields.phone,
        field: "Phone No. ",
        key: "phone",
        custom: () => {
          if (!validatePhone(fields.phone)) {
            return "Phone No is invalid.";
          }
          return true;
        },
      },
      {
        required: true,
        value: fields.gender,
        field: "Gender",
        key: "gender",
      },
      {
        required: true,
        value: fields.dob,
        field: "Date of birth",
        key: "dob",
      },
      {
        required: true,
        value: fields.address_line,
        field: "Address ",
        key: "address_line",
      },
      {
        value: fields.PIN,
        field: "PIN ",
        key: "PIN",
        custom: () => {
          if (fields.PIN && fields.PIN !== "" && !/^\d{6}$/.test(fields.PIN)) {
            return "Invalid PIN";
          }
          return true;
        },
      },
    ],
    [fields]
  );

  useEffect(() => {
    beneficiaryFormData.set("camp", fields.camp);
  }, [fields.camp]);

  useEffect(() => {
    beneficiaryFormData.set("product", fields.product);
  }, [fields.product]);

  useEffect(() => {
    beneficiaryFormData.set("country", fields.country);
  }, [fields.country]);

  useEffect(() => {
    beneficiaryFormData.set("state", fields.state);
  }, [fields.state]);

  useEffect(() => {
    beneficiaryFormData.set("district", fields.district);
  }, [fields.district]);

  useEffect(() => {
    beneficiaryFormData.set("suggested_product", fields.suggested_products);
  }, [fields.suggested_products]);

  const createFunction = async () => {
    const validationResponse = validate(validationSchemaForCreate);

    if (validationResponse === true) {
      setFieldErrors({});

      const passedData = {};
      const currentFields = { ...fields };
      for (const item in currentFields) {
        passedData[item] =
          currentFields[item] && typeof currentFields[item] === "object"
            ? currentFields[item]["_id"]
            : currentFields[item];
      }

      setLoading(true);
      dispatch(
        callApiAction(
          async () => await addBeneficiaryApi(passedData),
          async (response) => {
            setLoading(false);
            setFieldErrors({});
            setFields({
              ...response,
              camp: fields.camp,
              country: fields.country,
              state: fields.state,
              district: fields.district,
            });
            setStep(1);
          },
          (err) => {
            setLoading(false);
            setFieldErrors(getValidationErrors(validationSchemaForCreate));
            setFields({ ...fields, err });
            dispatch(callSnackBar(err, SNACK_BAR_VARIETNS.warning));
          }
        )
      );
      return;
    }

    setFieldErrors(getValidationErrors(validationSchemaForCreate));
    setFields({ ...fields, err: validationResponse });
    dispatch(callSnackBar(validationResponse, SNACK_BAR_VARIETNS.warning));
  };

  return (
    <CreateUi
      step={step}
      loading={loading}
      fields={fields}
      fieldErrors={fieldErrors}
      setFields={setFields}
      setFieldErrors={setFieldErrors}
      setStep={setStep}
      createFunction={createFunction}
    />
  );
};

export default CreateController;
